// Dark benefit + trust panel for the split sign-up / gate screens (ENG-3642,
// modeled on Town's live sign-up): the moment we ask for identity, the
// adjacent panel sells what signing up buys — client-ready deliverables in
// the firm's house style — with the trust proof points rendered right under
// the pitch. Rendered by /login today; the domain gate (ENG-3641) adopts the
// same exports when it lands.
//
// Copy rules:
//  - No retention claims that promise provider-side deletion, on any clock
//    (owner decision 2026-08-19, ENG-4294): "zero data retention" is banned
//    — the product runs on Managed Agents, which retain session state by
//    design — and "30-day deletion" is only true of Anthropic's base API
//    logs, not persisted session history (docs/MA_CONSTRAINTS.md). Safe
//    proof points: no training on customer content; server-enforced
//    workspace isolation. Canonical retention wording lives in
//    skills/product-help/topics/workspace/security.md.
//  - Positioning per docs/product-overview.md: every task ends in a concrete
//    artifact, not a chat transcript; M&A deal teams are the primary ICP.

// Deep Ocean in every workspace and OS color scheme, matching this panel's
// deliberately always-dark design.
const PANEL_BG = "var(--panel-background, #01201f)";
const PANEL_INK = "#eaf5f4";
const PANEL_ACCENT = "#d8e7ba";

const iconProps = {
  width: 20,
  height: 20,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.7,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

// Lucide-derived shapes, same convention as components/icons.tsx.
const BENEFITS: { title: string; body: string; icon: React.ReactNode }[] = [
  {
    title: "Finished work product, not chat transcripts",
    // "House style" lives in the next point, scoped to decks — the brand kit
    // styles presentations, not every file type (codex P1, PR #2047).
    // Leads with what paying customers actually ship (ENG-5571: 60-day sweep,
    // .pptx is ~58% of downloads — CIMs, teasers, pitch and investor decks).
    body: "CIMs, teasers, pitch and investor decks — plus the models and memos behind them. Every task ends in a client-ready file, ready to open and send.",
    icon: (
      <svg {...iconProps} aria-hidden="true">
        <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z" />
        <path d="M15 2v5h5" />
        <path d="m9 15 2 2 4-4" />
      </svg>
    ),
  },
  {
    title: "A workspace that knows your firm",
    body: "Projects and memory carry your firm's context into every task, and your brand kit into every deck — the work compounds instead of starting over.",
    icon: (
      <svg {...iconProps} aria-hidden="true">
        <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.93 0 1.65-.75 1.65-1.69 0-.44-.18-.84-.44-1.13-.29-.29-.44-.65-.44-1.12a1.64 1.64 0 0 1 1.67-1.67h2a5.55 5.55 0 0 0 5.55-5.55C21.97 6.01 17.46 2 12 2z" />
        <circle cx="13.5" cy="6.5" r="0.5" fill="currentColor" />
        <circle cx="17.5" cy="10.5" r="0.5" fill="currentColor" />
        <circle cx="8.5" cy="7.5" r="0.5" fill="currentColor" />
        <circle cx="6.5" cy="12.5" r="0.5" fill="currentColor" />
      </svg>
    ),
  },
  {
    // Uploads, not connectors, are how customers bring context (ENG-5571:
    // ~19% of messages carry files; a handful of users connected a tool).
    title: "Built on your deal files",
    body: "Upload the financials, drafts, and diligence material you already have — or connect your tools — and the agent works from what you give it.",
    icon: (
      <svg {...iconProps} aria-hidden="true">
        <ellipse cx="12" cy="5" rx="9" ry="3" />
        <path d="M3 5v14a9 3 0 0 0 18 0V5" />
        <path d="M3 12a9 3 0 0 0 18 0" />
      </svg>
    ),
  },
];

/**
 * Trust proof points (the ticket's non-negotiables). Rendered as a compact
 * checklist under the benefits on desktop, and as the standalone strip on
 * mobile. No retention/deletion promises here — see the header comment;
 * isolation is the strongly-evidenced claim (server-enforced tenancy). Every
 * point must be a shipped capability stated in
 * skills/product-help/topics/workspace/security.md — "cell-level source
 * traceability" was a roadmap item, not a product fact (ENG-5571).
 */
const TRUST_POINTS: string[] = [
  "No training on your client material",
  "Server-enforced workspace isolation",
  "Role-based access, enforced on the server",
];

function CheckIcon({ className = "" }: { className?: string }) {
  return (
    <svg {...iconProps} width={16} height={16} className={className} aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

/**
 * The dark benefit + trust panel — one side of the split screen. The caller
 * owns sizing/visibility (e.g. `hidden lg:flex lg:w-[45%]`); the panel fills
 * whatever box it's given.
 */
export function TrustPanel({ className = "" }: { className?: string }) {
  return (
    <aside
      // No overflow-hidden here: on short lg viewports the panel must grow
      // and let the page scroll rather than clip the proof points (codex P2).
      className={`relative flex-col px-10 py-10 lg:py-14 xl:px-16 ${className}`}
      style={{ backgroundColor: PANEL_BG, color: PANEL_INK }}
    >
      {/* Ambient sage glow + hairline grid — CSS-only texture so the dark side
          reads designed rather than flat. */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(90% 70% at 80% 0%, rgba(216,231,186,0.12), transparent 60%)," +
            "radial-gradient(70% 60% at 0% 100%, rgba(20,109,96,0.18), transparent 55%)," +
            "linear-gradient(rgba(234,245,244,0.035) 1px, transparent 1px)," +
            "linear-gradient(90deg, rgba(234,245,244,0.035) 1px, transparent 1px)",
          backgroundSize: "auto, auto, 44px 44px, 44px 44px",
        }}
      />

      {/* my-auto instead of justify-center on the aside: auto margins center
          when there's room but collapse to 0 on overflow, so short viewports
          scroll from the top instead of clipping the eyebrow (codex P2). */}
      <div className="relative max-w-md my-auto">
        <p
          className="text-[11px] font-semibold uppercase tracking-[0.18em]"
          style={{ color: PANEL_ACCENT }}
        >
          Your AI deal partner
        </p>
        {/* Mirrors the deliverables.ai tagline (GTM-455) so a visitor who
            clicks "Start free" lands on the same promise (ENG-5571). */}
        {/* Whole-workspace framing (owner direction, 2026-08-01): the product
            philosophy from docs/product-overview.md — every task ends in a
            concrete artifact — not a single deck-speed claim. */}
        <h2 className="mt-3 text-3xl xl:text-[2.35rem] font-semibold leading-tight tracking-tight">
          Every task ends in a deliverable.
        </h2>

        <ul className="mt-9 flex flex-col gap-6">
          {BENEFITS.map((b) => (
            <li key={b.title} className="flex gap-4">
              <span
                className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border"
                style={{
                  color: PANEL_ACCENT,
                  borderColor: "rgba(234,245,244,0.14)",
                  backgroundColor: "rgba(234,245,244,0.05)",
                }}
              >
                {b.icon}
              </span>
              <span>
                <span className="block text-sm font-semibold">{b.title}</span>
                <span
                  className="mt-1 block text-sm leading-relaxed"
                  style={{ color: "rgba(234,245,244,0.68)" }}
                >
                  {b.body}
                </span>
              </span>
            </li>
          ))}
        </ul>

        <div
          className="mt-10 border-t pt-6"
          style={{ borderColor: "rgba(234,245,244,0.12)" }}
        >
          <p className="text-sm font-semibold">
            And your client material stays yours
          </p>
          <ul className="mt-3 flex flex-col gap-2">
            {TRUST_POINTS.map((t) => (
              <li
                key={t}
                className="flex items-center gap-2.5 text-sm"
                style={{ color: "rgba(234,245,244,0.68)" }}
              >
                <CheckIcon className="shrink-0" />
                {t}
              </li>
            ))}
          </ul>
          <p
            className="mt-5 text-xs leading-relaxed"
            style={{ color: "rgba(234,245,244,0.5)" }}
          >
            Formal security documentation is available on request.
          </p>
        </div>
      </div>
    </aside>
  );
}

/**
 * Compact single-column rendering of the trust points, for viewports too
 * narrow for the split (the proof must still appear at the moment of the
 * ask, even on a phone).
 */
export function TrustPointsCompact() {
  return (
    <ul className="flex flex-col gap-2.5">
      {TRUST_POINTS.map((t) => (
        <li key={t} className="flex items-center gap-2.5 text-xs text-gray-600">
          <span className="shrink-0 text-[#146d60]">
            <CheckIcon />
          </span>
          {t}
        </li>
      ))}
    </ul>
  );
}

/**
 * Split shell: auth (or gate) content on the left, dark benefit + trust
 * panel on the right; stacks to a single column with the compact proof strip
 * below the content on small screens.
 */
export function TrustSplitShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex bg-white">
      <div className="flex flex-1 flex-col">
        <main className="flex flex-1 items-center justify-center px-4 py-10 sm:px-6">
          <div className="w-full max-w-sm">{children}</div>
        </main>
        <div className="lg:hidden border-t border-gray-100 bg-gray-50/60 px-6 py-5">
          <div className="mx-auto max-w-sm">
            <TrustPointsCompact />
          </div>
        </div>
      </div>
      <TrustPanel className="hidden lg:flex lg:w-[46%] xl:w-[44%]" />
    </div>
  );
}
